import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MY_OWN_FILTER, TransactionFilters } from '../constants/constants';
import { useUser } from '../context/UserContext';
import { IFilterOption } from '../interface/transaction';

const OWNER_FILTER_VALUES = new Set<string>([MY_OWN_FILTER, TransactionFilters.all]);

const resolveInitialValue = (isMyOwnDefault: boolean, restoredValue?: string | null): string => {
  if (restoredValue && OWNER_FILTER_VALUES.has(restoredValue)) return restoredValue;
  return isMyOwnDefault ? MY_OWN_FILTER : TransactionFilters.all;
};

export const buildOwnerUserFilters = (userId: string): Record<string, string> => ({
  ownerUserId: `$or:$eq:${userId}`,
  responsibleUserId: `$or:$eq:${userId}`,
  assistantUserId: `$or:$eq:${userId}`,
  invoicingUserId: `$or:$eq:${userId}`,
});

export const useOwnerFilter = ({
  isMyOwnDefault,
  restoredValue,
}: {
  isMyOwnDefault: boolean;
  restoredValue?: string | null;
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [value, setValue] = useState<string>(() =>
    resolveInitialValue(isMyOwnDefault, restoredValue),
  );

  const options: IFilterOption[] = [
    { label: t('common:ownerFilter.myOwn'), value: MY_OWN_FILTER },
    { label: t('common:ownerFilter.all'), value: TransactionFilters.all },
  ];
  const isMyOwn = value === MY_OWN_FILTER;

  return {
    value,
    isMyOwn,
    options,
    title: t('common:ownerFilter.label'),
    selectedOptions: options.filter((option) => option.value === value),
    // Only for lists the API has no `/my` endpoint for.
    apiFilters: isMyOwn && user?.id ? buildOwnerUserFilters(user.id) : {},
    select: (option: IFilterOption) => setValue(option.value),
    reset: () => setValue(TransactionFilters.all),
  };
};
